import { useMemo } from 'react';
import { Tooltip } from 'antd';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';

import { Close } from '@assets/icons';
import { Button } from '@components/Button';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import { useGetDefaultJobsBoardFilters } from '@hooks/jobsBoard/useGetDefaultJobsBoardFilters';
import { useUpsertJobsBoardDefaultFilters } from '@hooks/jobsBoard/useUpsertJobsBoardDefaultFilters';
import { buildPayloadSearchFilters } from '@hooks/jobsBoard/utils';
import { UseModalVisibilityHookReturn } from '@hooks/shared/useModalVisibility';
import { RootState } from '@lib/redux.lib/store';
import { useJobsBoardFiltersViewContext } from '@views/jobsBoard/context/JobsBoardFiltersViewContext';

import { FilterDivider } from './components/FilterDivider';
import { AgeRangeFilter } from './AgeRangeFilter';
import {
  JobsBoardFiltersModalContextProvider,
  useJobsBoardFiltersModalContext,
} from './context';
import { EthnicityFilter } from './EthnicityFilter';
import { GenderFilter } from './GenderFilter';
import { JobPostStatusFilter } from './JobPostStatusFilter';
import { ProjectLocationFilter } from './ProjectLocationFilter';
import { RoleTypeFilter } from './RoleTypeFilter';
import { StyledBody, StyledFooter, StyledHeader, StyledModal } from './styles';
import { UnionStatusFilter } from './UnionStatusFilter';
import { WorkDatesFilter } from './WorkDatesFilter';

type JobsBoardFiltersModalProps = {
  jobsFiltersModalVisibility: UseModalVisibilityHookReturn;
};

export const JobsBoardFiltersModal = ({
  jobsFiltersModalVisibility,
}: JobsBoardFiltersModalProps) => {
  return (
    <StyledModal
      open={jobsFiltersModalVisibility.isVisible}
      onCancel={jobsFiltersModalVisibility.hideModal}
      footer={null}
      centered
      destroyOnClose
      closeIcon={<Close />}
    >
      <JobsBoardFiltersModalContextProvider
        jobsFiltersModalVisibility={jobsFiltersModalVisibility}
      >
        <ModalFiltersContent
          jobsFiltersModalVisibility={jobsFiltersModalVisibility}
        />
      </JobsBoardFiltersModalContextProvider>
    </StyledModal>
  );
};

type ModalFiltersContentProps = {
  jobsFiltersModalVisibility: UseModalVisibilityHookReturn;
};

const ModalFiltersContent = ({
  jobsFiltersModalVisibility,
}: ModalFiltersContentProps) => {
  const { resetAllFilters, isResetAllButtonDisabled } =
    useJobsBoardFiltersModalContext();
  const { mainViewCountry } = useJobsBoardFiltersViewContext();

  return (
    <>
      <StyledHeader>
        <UnstyledButton
          disabled={isResetAllButtonDisabled}
          className='reset-all-button'
          onClick={resetAllFilters}
        >
          Reset All
        </UnstyledButton>
        <div>Filters</div>
        <div className='close-button-container'>
          <UnstyledButton
            onClick={jobsFiltersModalVisibility.hideModal}
            className='close-button'
          >
            <Close />
          </UnstyledButton>
        </div>
      </StyledHeader>

      <StyledBody>
        <ProjectLocationFilter />
        <FilterDivider />
        <RoleTypeFilter />
        <FilterDivider />
        <WorkDatesFilter />
        <FilterDivider />
        <AgeRangeFilter />
        <FilterDivider />
        <GenderFilter />
        <FilterDivider />
        <EthnicityFilter />
        {mainViewCountry === 'US' && (
          <>
            <FilterDivider />
            <UnionStatusFilter />
          </>
        )}

        <FilterDivider />
        <JobPostStatusFilter />
      </StyledBody>
      <ModalFooter />
    </>
  );
};

const ModalFooter = () => {
  const {
    resetAllFilters,
    isResetAllButtonDisabled,
    onApplyFilters,
    modalFilters,
  } = useJobsBoardFiltersModalContext();

  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );

  const defaultFiltersPayload = useMemo(() => {
    return buildPayloadSearchFilters(modalFilters);
  }, [modalFilters]);

  const { upsertJobsBoardDefaultFilters, isUpsertingJobsBoardDefaultFilters } =
    useUpsertJobsBoardDefaultFilters(defaultFiltersPayload);

  const { data: defaultFilters } = useGetDefaultJobsBoardFilters();

  const isSaveFiltersDisabled = useMemo(() => {
    if (
      !defaultFilters ||
      isUpsertingJobsBoardDefaultFilters ||
      !loggedInUserId
    ) {
      return true;
    }
    return isEqual(defaultFilters, defaultFiltersPayload);
  }, [
    isUpsertingJobsBoardDefaultFilters,
    defaultFilters,
    defaultFiltersPayload,
    loggedInUserId,
  ]);

  return (
    <StyledFooter>
      <UnstyledButton
        className='reset-all-button'
        disabled={isResetAllButtonDisabled}
        onClick={resetAllFilters}
      >
        Reset All
      </UnstyledButton>
      <div className='buttons-container'>
        <Tooltip
          title={!loggedInUserId ? 'Log in to use this feature' : undefined}
        >
          <div>
            <Button
              className='footer-button save-button'
              disabled={isSaveFiltersDisabled}
              onClick={() => upsertJobsBoardDefaultFilters()}
            >
              Save Filters as Default
            </Button>
          </div>
        </Tooltip>

        <Button
          type='primary'
          className='footer-button apply-button'
          onClick={onApplyFilters}
        >
          Apply
        </Button>
      </div>
    </StyledFooter>
  );
};
